import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Badge, Modal, EmptyState, LoadingSkeleton } from './ui';
import { DocumentTextIcon, ArrowDownTrayIcon, ArrowUpTrayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { hasAnyRole } from '../utils/roles';
import { safeErrorMessage } from '../utils/apiError';
import styles from './Documents.module.css';

const MANAGE_ROLES = ['administrator', 'principal'];
const CATEGORY_LABELS = {
  newsletter: 'Newsletter',
  circular: 'Circular',
  fee_structure: 'Fee structure',
  policy: 'Policy',
  calendar: 'Calendar',
  form: 'Form',
  other: 'Other',
};
// Mirrors backend/documents.py's MAX_DOCUMENT_BYTES / ALLOWED_DOCUMENT_TYPES
const MAX_BYTES = 5 * 1024 * 1024;
const ACCEPT = '.pdf,.docx,.xlsx,.png,.jpg,.jpeg';
const EMPTY_UPLOAD = { title: '', category: 'circular', description: '', file: null, audience: 'all', classIds: [] };

function formatSize(bytes) {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function formatDate(iso) {
  return iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
}

function Documents({ token, user }) {
  const canManage = hasAnyRole(user, MANAGE_ROLES);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [category, setCategory] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [upload, setUpload] = useState(EMPTY_UPLOAD);
  const [uploadError, setUploadError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [classes, setClasses] = useState([]);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/v1/documents${category ? `?category=${category}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setDocuments(await response.json());
      } else {
        setError(await safeErrorMessage(response, 'Failed to load documents'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [token, category]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const openUpload = async () => {
    setUpload(EMPTY_UPLOAD);
    setUploadError('');
    setUploadOpen(true);
    if (classes.length === 0) {
      try {
        const response = await fetch('/api/v1/classes', { headers: { Authorization: `Bearer ${token}` } });
        if (response.ok) setClasses(await response.json());
      } catch {
        // Class targeting just stays unavailable; the upload itself still works.
      }
    }
  };

  const toggleClass = (id) => {
    setUpload((prev) => ({
      ...prev,
      classIds: prev.classIds.includes(id) ? prev.classIds.filter((c) => c !== id) : [...prev.classIds, id],
    }));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!upload.file) {
      setUploadError('Choose a file to upload.');
      return;
    }
    if (upload.file.size > MAX_BYTES) {
      setUploadError('File must be smaller than 5MB.');
      return;
    }
    if (upload.audience === 'classes' && upload.classIds.length === 0) {
      setUploadError('Select at least one class, or choose "All classes".');
      return;
    }

    setUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', upload.file);
      formData.append('title', upload.title);
      formData.append('category', upload.category);
      formData.append('description', upload.description);
      if (upload.audience === 'classes') formData.append('class_ids', JSON.stringify(upload.classIds));

      const response = await fetch('/api/v1/documents', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (response.ok) {
        setUploadOpen(false);
        fetchDocuments();
      } else {
        setUploadError(await safeErrorMessage(response, 'Failed to upload document'));
      }
    } catch {
      setUploadError('Network error. Please check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  // The download needs the Authorization header, so it can't be a plain link.
  const handleDownload = async (doc) => {
    setDownloadingId(doc.id);
    setError('');
    try {
      const response = await fetch(`/api/v1/documents/${doc.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) {
        setError(await safeErrorMessage(response, 'Failed to download document'));
        return;
      }
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await fetch(`/api/v1/documents/${deleteTarget.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setDocuments((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      } else {
        setError(await safeErrorMessage(response, 'Failed to delete document'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const columns = [
    {
      header: 'Document',
      render: (row) => (
        <div>
          <div className={styles.docTitle}>{row.title}</div>
          {row.description && <div className={styles.docDescription}>{row.description}</div>}
        </div>
      ),
    },
    { header: 'Category', render: (row) => <Badge variant="info">{CATEGORY_LABELS[row.category] || row.category}</Badge> },
    { header: 'Added', render: (row) => formatDate(row.created_at) },
    { header: 'Size', render: (row) => formatSize(row.size_bytes) },
  ];
  if (canManage) {
    columns.splice(2, 0, { header: 'For', render: (row) => (row.class_names?.length ? row.class_names.join(', ') : 'All classes') });
  }
  columns.push({
    header: '',
    render: (row) => (
      <div className={styles.rowActions}>
        <Button variant="secondary" size="sm" onClick={() => handleDownload(row)} disabled={downloadingId === row.id}>
          <ArrowDownTrayIcon style={{ width: 14, height: 14 }} />
          {downloadingId === row.id ? 'Downloading…' : 'Download'}
        </Button>
        {canManage && (
          <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(row)} aria-label={`Delete ${row.title}`}>
            <TrashIcon style={{ width: 14, height: 14, color: 'var(--color-danger)' }} />
          </Button>
        )}
      </div>
    ),
  });

  return (
    <div>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>School Documents</h2>
          <p className={styles.subtitle}>
            {canManage ? 'Newsletters, circulars, fee structures and policies shared with parents and staff.' : 'Newsletters, circulars and other documents from the school.'}
          </p>
        </div>
        <div className={styles.controls}>
          <select className={styles.select} value={category} onChange={(e) => setCategory(e.target.value)} aria-label="Filter by category">
            <option value="">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          {canManage && (
            <Button variant="primary" size="sm" onClick={openUpload}>
              <ArrowUpTrayIcon style={{ width: 16, height: 16 }} />
              Upload document
            </Button>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <Card>
        {loading ? (
          <LoadingSkeleton type="text" rows={6} />
        ) : documents.length === 0 ? (
          <EmptyState
            title={category ? 'No documents in this category' : 'No documents yet'}
            message={canManage ? 'Upload a document to share it with parents and staff.' : 'Documents shared by the school will appear here.'}
            icon={<DocumentTextIcon />}
          />
        ) : (
          <Table columns={columns} data={documents} />
        )}
      </Card>

      <Modal isOpen={uploadOpen} onClose={() => setUploadOpen(false)} title="Upload document" width="540px">
        <form onSubmit={handleUpload}>
          {uploadError && <div className="alert alert-error">{uploadError}</div>}
          <div className="form-group">
            <label htmlFor="document-title">Title</label>
            <input id="document-title" type="text" value={upload.title} onChange={(e) => setUpload((prev) => ({ ...prev, title: e.target.value }))} required maxLength={200} />
          </div>
          <div className="form-group">
            <label htmlFor="document-category">Category</label>
            <select id="document-category" value={upload.category} onChange={(e) => setUpload((prev) => ({ ...prev, category: e.target.value }))}>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="document-description">Description (optional)</label>
            <textarea id="document-description" rows={2} value={upload.description} onChange={(e) => setUpload((prev) => ({ ...prev, description: e.target.value }))} />
          </div>
          <div className="form-group">
            <label htmlFor="document-file">File</label>
            <input id="document-file" type="file" accept={ACCEPT} onChange={(e) => setUpload((prev) => ({ ...prev, file: e.target.files[0] || null }))} />
            <small>PDF, Word (.docx), Excel (.xlsx), PNG or JPEG, up to 5MB.</small>
          </div>

          <div className={styles.choiceGroup}>
            <span className={styles.choiceLabel}>Which parents can see it?</span>
            <label className={styles.choice}>
              <input type="radio" name="audience" checked={upload.audience === 'all'} onChange={() => setUpload((prev) => ({ ...prev, audience: 'all' }))} />
              All classes
            </label>
            <label className={styles.choice}>
              <input type="radio" name="audience" checked={upload.audience === 'classes'} onChange={() => setUpload((prev) => ({ ...prev, audience: 'classes' }))} />
              Only selected classes
            </label>
          </div>
          {upload.audience === 'classes' && (
            classes.length === 0 ? (
              <p className={styles.hint}>No classes have been set up yet.</p>
            ) : (
              <div className={styles.classGrid}>
                {classes.map((schoolClass) => (
                  <label key={schoolClass.id} className={styles.choice}>
                    <input type="checkbox" checked={upload.classIds.includes(schoolClass.id)} onChange={() => toggleClass(schoolClass.id)} />
                    {schoolClass.display_name}
                  </label>
                ))}
              </div>
            )
          )}
          <p className={styles.hint} style={{ marginTop: '0.75rem' }}>Staff can always see every document.</p>

          <div className={styles.modalActions}>
            <Button variant="primary" type="submit" disabled={uploading}>{uploading ? 'Uploading…' : 'Upload'}</Button>
            <Button variant="secondary" onClick={() => setUploadOpen(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Delete document">
        {deleteTarget && (
          <div>
            <p className={styles.hint}>
              Delete <strong>{deleteTarget.title}</strong>? Parents and staff will no longer be able to download it. This cannot be undone.
            </p>
            <div className={styles.modalActions}>
              <Button variant="danger" onClick={handleDelete} disabled={deleting}>{deleting ? 'Deleting…' : 'Delete document'}</Button>
              <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Keep it</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default Documents;
